import { useAuth, useContentResource } from "@ctv/core";
import {
  GENERIC_BANNER_CONFIG,
  useS3FeatureControl,
} from "@ctv/shared-core/src";
import { BannerProps } from "@ctv/shared-core/src/components/Banner/Banner";
import {
  get as getLocalStorageUserLevel,
  set as setLocalStorageUserLevel,
} from "@ctv/shared/hooks/useLocalStorageUserLevel";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

export default function useGenericBanner() {
  const pathname = usePathname();
  const { user } = useAuth();
  const [banner, setBanner] = useState<BannerProps[]>([]);
  const { isEnabled, config } = useS3FeatureControl(GENERIC_BANNER_CONFIG);
  const { CorporateGenericBanner } = useContentResource();

  const handleCloseBanner = useCallback(
    (id: string | number) => {
      if (!user) return;
      setLocalStorageUserLevel(
        genericBannerLocalStorageKey(id.toString()),
        user
      );
    },
    [user]
  );

  useEffect(() => {
    if (
      !user ||
      !isEnabled ||
      !config ||
      !config.banner ||
      config.banner.length === 0
    ) {
      return;
    }

    const unseenBanners = config.banner.filter((bannerItem) => {
      const hasSeen = getLocalStorageUserLevel(
        genericBannerLocalStorageKey(bannerItem.id),
        user
      );
      return !hasSeen;
    });

    const banner = unseenBanners
      .filter((item) => {
        const matchRecepient =
          item.spec.recepient.global ||
          item.spec.recepient.corporateCode?.includes(user.corporateCode);

        const matchPage =
          item.spec.pages.global ||
          item.spec.pages.name?.some((pattern) =>
            wildcardMatch(pathname, pattern)
          );

        return matchRecepient && matchPage;
      })
      .map((item) => {
        return {
          id: item.id,
          type: item.content.type,
          content:
            CorporateGenericBanner[`${item.content.crKey}`] ||
            item.content.crKey,
        };
      });

    setBanner(banner);
  }, [pathname, isEnabled, config, user]);

  return { banner, handleCloseBanner };
}

function wildcardMatch(path: string, pattern: string): boolean {
  const regex = new RegExp(
    "^" +
      pattern
        .replace(/\//g, "\\/") // Escape slashes
        .replace(/\[locale\]/g, "[^/]+") // Dynamic locale segment
        .replace(/\*/g, ".*") + // Wildcard match
      "$"
  );
  return regex.test(path);
}

function genericBannerLocalStorageKey(id: string): string {
  return `gb-${id}`;
}
